
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MapPin, ArrowLeft, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreatePost() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState({ lat: 19.076, lng: 72.8777 });
  const [currentTag, setCurrentTag] = useState('');
  
  // Check if opened from "For Sale" page
  const queryParams = new URLSearchParams(location.search);
  const presetCategory = queryParams.get('category');
  const isSimpleMode = !presetCategory; // Simple mode when no category specified

  const [postData, setPostData] = useState({
    title: '',
    description: '',
    tags: [],
    category: presetCategory || 'sale',
    latitude: 19.076,
    longitude: 72.8777,
    radius: 5000,
    images: []
  });

  useEffect(() => {
    // Check for saved location first
    const savedLocation = localStorage.getItem('userLocation');
    if (savedLocation) {
      const loc = JSON.parse(savedLocation);
      setUserLocation(loc);
      setPostData(prev => ({ ...prev, latitude: loc.lat, longitude: loc.lng }));
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const location = { lat, lng };
          setUserLocation(location);
          setPostData(prev => ({ ...prev, latitude: lat, longitude: lng }));
          localStorage.setItem('userLocation', JSON.stringify(location));
        },
        (error) => {
          console.error('Geolocation error:', error);
          toast.error('Using default location');
        }
      );
    }
  }, []);

  const handleAddTag = () => {
    if (currentTag.trim() && !postData.tags.includes(currentTag.trim())) {
      setPostData(prev => ({
        ...prev,
        tags: [...prev.tags, currentTag.trim()]
      }));
      setCurrentTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setPostData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (postData.tags.length < 5) {
      toast.error('Please add at least 5 tags');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/posts`, postData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="glassmorphism border-b border-stone-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              onClick={() => navigate('/dashboard')}
              variant="outline"
              size="icon"
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <MapPin className="w-8 h-8 text-orange-600" />
              <h1 className="text-2xl font-bold text-stone-900">Create Post</h1>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card data-testid="create-post-form">
          <CardHeader>
            <CardTitle>Share with Your Community</CardTitle>
            <CardDescription>
              Post your need, offer help, or share something with neighbors nearby
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title">Post Title *</Label>
                <Input
                  id="title"
                  data-testid="post-title-input"
                  placeholder="e.g., Need a plumber urgently"
                  value={postData.title}
                  onChange={(e) => setPostData({ ...postData, title: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  data-testid="post-description-input"
                  placeholder="Describe your requirement in detail..."
                  rows={5}
                  value={postData.description}
                  onChange={(e) => setPostData({ ...postData, description: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={postData.category}
                  onValueChange={(value) => setPostData({ ...postData, category: value })}
                >
                  <SelectTrigger data-testid="post-category-select" id="category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="help">Help</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="sale">Sale</SelectItem>
                    <SelectItem value="donation">Donation</SelectItem>
                    <SelectItem value="community">Community</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Images (Optional)</Label>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    const readers = files.map(file => {
                      return new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.readAsDataURL(file);
                      });
                    });
                    Promise.all(readers).then(images => {
                      setPostData({ ...postData, images: images.slice(0, 5) });
                      toast.success(`${images.length} image(s) added`);
                    });
                  }}
                  className="cursor-pointer"
                />
                <p className="text-sm text-stone-500">Upload up to 5 images</p>
                {postData.images.length > 0 && (
                  <div className="flex gap-2 flex-wrap mt-2">
                    {postData.images.map((img, idx) => (
                      <div key={idx} className="relative w-20 h-20">
                        <img src={img} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => {
                            const newImages = postData.images.filter((_, i) => i !== idx);
                            setPostData({ ...postData, images: newImages });
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Tags * (Minimum 5 required)</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="tag-input"
                    placeholder="Add a tag"
                    value={currentTag}
                    onChange={(e) => setCurrentTag(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                  />
                  <Button
                    data-testid="add-tag-btn"
                    type="button"
                    onClick={handleAddTag}
                    variant="outline"
                    size="icon"
                  >
                    <Plus className="w-5 h-5" />
                  </Button>
                </div>
                
                {postData.tags.length > 0 && (
                  <div data-testid="tags-container" className="flex flex-wrap gap-2 mt-3">
                    {postData.tags.map((tag, i) => (
                      <Badge key={i} variant="secondary" className="px-3 py-2 bg-stone-100 text-stone-600">
                        #{tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-2 hover:text-orange-900"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-sm text-stone-500">
                  {postData.tags.length}/5 tags added
                  {postData.tags.length < 5 && ` (${5 - postData.tags.length} more needed)`}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="radius">Visibility Radius *</Label>
                <Select
                  value={postData.radius.toString()}
                  onValueChange={(value) => setPostData({ ...postData, radius: parseInt(value) })}
                >
                  <SelectTrigger data-testid="post-radius-select" id="radius">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1000">1 km</SelectItem>
                    <SelectItem value="5000">5 km</SelectItem>
                    <SelectItem value="10000">10 km</SelectItem>
                    <SelectItem value="25000">25 km</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-stone-500">
                  People within this radius will see your post
                </p>
              </div>

              <div className="bg-orange-50 p-4 rounded-xl border border-orange-200">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-orange-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-stone-900">Location Detected</p>
                    <p className="text-xs text-stone-600 mt-1">
                      Lat: {postData.latitude.toFixed(4)}, Lng: {postData.longitude.toFixed(4)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  data-testid="cancel-btn"
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="submit-post-btn"
                  type="submit"
                  disabled={loading || postData.tags.length < 5}
                  className="flex-1 rounded-full btn-primary"
                >
                  {loading ? 'Creating...' : 'Create Post'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
